import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'umi';
import { fetchContactsRequest } from '@/services/api/contact';
import { processAPIResponseNow } from '@/utils/request';
import { ContactModel } from '@/typings/models/Contact';
import { collectKnownRoles, extractContactList } from '@/utils/contacts';
import { selectContactRoles } from '@/selectors/preferencesSelectors';

/** The API's page maximum: the most recent contacts carry the roles in use. */
const ROLE_SAMPLE_SIZE = 250;

// One request per page load; roles change rarely.
let partnerRoles: Promise<string[]> | null = null;

const loadPartnerRoles = () => {
  if (!partnerRoles) {
    partnerRoles = fetchContactsRequest({ query: { page_size: ROLE_SAMPLE_SIZE, skip_total_size: 1 } }).then(
      (response) =>
        new Promise<string[]>((resolve) => {
          processAPIResponseNow(response, {
            onSuccess: (body: unknown) => resolve(collectKnownRoles(extractContactList(body).items)),
            // Suggestions are a convenience: without them the field is still free text.
            onError: () => resolve([]),
          });
        }),
    );
  }
  return partnerRoles;
};

/** Roles already given to the partner's contacts, most used first. */
export const usePartnerRolesInUse = (): string[] => {
  const [roles, setRoles] = useState<string[]>([]);

  useEffect(() => {
    let active = true;
    loadPartnerRoles().then((loaded) => active && setRoles(loaded));
    return () => {
      active = false;
    };
  }, []);

  return roles;
};

/**
 * Role suggestions: the partner's own list (Settings → Data Entry) in its
 * order, then roles in use across the partner's contacts, topped up with the
 * contacts at hand. Any other role can still be typed.
 */
export const useContactRoleSuggestions = (contacts: ContactModel[] = []): string[] => {
  const configured = useSelector(selectContactRoles);
  const inUse = usePartnerRolesInUse();

  return useMemo(() => [...configured, ...inUse, ...collectKnownRoles(contacts)], [configured, inUse, contacts]);
};
